import React, { useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { DateTimePicker } from '../inputs/date-time/DateTimePicker';
import { getMoment } from 'utils/DateTimeUtils';
import { CalendarRange } from 'components/inputs/date-time/CalendarRange';
import { RollingDateFilter } from './RollingDateFilter';
import { IDateRangeFilterLayerProps } from './DateRangeFilterLayer';
import { DateRangeTypes } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/DateRangeTypes';
import { newReportDateRange } from '@decisions/server/DecisionsFramework/Design/Flow/CoreSteps/Metro/ReportDateRange';

export enum DateRangeOption {
  Previous = 1,
  Current,
  Future,
  Custom,
  Rolling,
}

export enum DateSelectionType {
  Week = 1,
  Month,
  Quarter,
  Year,
}

interface SelectedOptions {
  dateSelectionType?: DateSelectionType;
  dateRangeOption?: DateRangeOption;
}

interface ICustomDateRangeFilterProps extends IDateRangeFilterLayerProps {}

const getDateRange = (dateRangeOption?: DateRangeOption, selectionType?: DateSelectionType) => {
  let unitOfTime: 'week' | 'month' | 'year' | 'quarter' | 'day' = 'day';

  switch (selectionType) {
    case DateSelectionType.Week:
      unitOfTime = 'week';
      break;
    case DateSelectionType.Month:
      unitOfTime = 'month';
      break;
    case DateSelectionType.Quarter:
      unitOfTime = 'quarter';
      break;
    case DateSelectionType.Year:
      unitOfTime = 'year';
      break;
  }

  if (unitOfTime) {
    let date = getMoment()().startOf(unitOfTime as moment.unitOfTime.StartOf);
    switch (dateRangeOption) {
      case DateRangeOption.Previous:
        date = date.subtract(1, unitOfTime);
        break;
      case DateRangeOption.Future:
        date = date.add(1, unitOfTime);
        break;
    }

    return {
      startDate: date.startOf(unitOfTime).clone(),
      endDate: date.endOf(unitOfTime).clone(),
    };
  }
};

const getDateRangeType = (dateRangeOption?: DateRangeOption, selectionType?: DateSelectionType) => {
  let dateRangeToSelect;
  switch (dateRangeOption) {
    case DateRangeOption.Custom:
      dateRangeToSelect = DateRangeTypes.Custom;
      break;
    case DateRangeOption.Rolling:
      dateRangeToSelect = DateRangeTypes.Rolling;
      break;
    case DateRangeOption.Previous:
      switch (selectionType) {
        case DateSelectionType.Week:
          dateRangeToSelect = DateRangeTypes.LastWeek;
          break;
        case DateSelectionType.Month:
          dateRangeToSelect = DateRangeTypes.LastMonth;
          break;
        case DateSelectionType.Quarter:
          dateRangeToSelect = DateRangeTypes.LastQuarter;
          break;
        case DateSelectionType.Year:
          dateRangeToSelect = DateRangeTypes.LastYear;
          break;
      }
      break;
    case DateRangeOption.Current:
      switch (selectionType) {
        case DateSelectionType.Week:
          dateRangeToSelect = DateRangeTypes.ThisWeek;
          break;
        case DateSelectionType.Month:
          dateRangeToSelect = DateRangeTypes.ThisMonth;
          break;
        case DateSelectionType.Quarter:
          dateRangeToSelect = DateRangeTypes.ThisQuarter;
          break;
        case DateSelectionType.Year:
          dateRangeToSelect = DateRangeTypes.ThisYear;
          break;
      }
      break;
    case DateRangeOption.Future:
      switch (selectionType) {
        case DateSelectionType.Week:
          dateRangeToSelect = DateRangeTypes.NextWeek;
          break;
        case DateSelectionType.Month:
          dateRangeToSelect = DateRangeTypes.NextMonth;
          break;
        case DateSelectionType.Quarter:
          dateRangeToSelect = DateRangeTypes.NextQuarter;
          break;
        case DateSelectionType.Year:
          dateRangeToSelect = DateRangeTypes.NextYear;
          break;
      }
      break;
  }

  return dateRangeToSelect;
};

export function CustomDateRangeFilter({
  dateRangeFilterData,
  onSelectionChange: onSelectionChangeHandler,
  disableRollingRange,
}: ICustomDateRangeFilterProps) {
  const [selectedOptions, setSelectedOptions] = useState(getSelectedOption(dateRangeFilterData?.DateRange));

  const option = selectedOptions?.dateRangeOption;
  const dateSelectionType = selectedOptions?.dateSelectionType;
  const dateRange = getDateRange(selectedOptions?.dateRangeOption, selectedOptions?.dateSelectionType);
  const initialStartDate =
    dateRangeFilterData?.DateRange === DateRangeTypes.Custom
      ? getMoment()(dateRangeFilterData?.FromDate)
      : dateRange?.startDate;
  const initialEndDate =
    dateRangeFilterData?.DateRange === DateRangeTypes.Custom
      ? getMoment()(initialStartDate).add(dateRangeFilterData?.NumberOfDays - 1, 'days')
      : dateRange?.endDate;
  const startDateString = initialStartDate?.toISOString();
  const endDateString = initialEndDate?.toISOString();
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateString, endDateString]);

  useEffect(() => {
    setSelectedOptions(getSelectedOption(dateRangeFilterData?.DateRange));
  }, [dateRangeFilterData?.DateRange]);

  const showDateSelectionType = option && option <= DateRangeOption.Future;
  const showDatePicker =
    (option && option === DateRangeOption.Custom) ||
    (showDateSelectionType && dateSelectionType && dateSelectionType >= DateSelectionType.Quarter);

  const getButtonClass = (type: DateRangeOption) => {
    return classNames('dpc-date-filter-layer__choice-button', 'dpc-date-filter-layer__choice-button--secondary', {
      'dpc-date-filter-layer__choice-button--active': type === option,
    });
  };

  const getDateSelectionTypeClass = (val: DateSelectionType) => {
    return classNames('dpc-date-filter-layer__selection-type', {
      'dpc-date-filter-layer__selection-type--active': val === dateSelectionType,
      'dpc-date-filter-layer__selection-type--no-seperator': val === DateSelectionType.Year,
    });
  };

  const onSelectionChange = (dateRangeOption?: DateRangeOption, selectionType?: DateSelectionType) => {
    const dateRange = getDateRange(dateRangeOption, selectionType);
    setStartDate(dateRange?.startDate);
    setEndDate(dateRange?.endDate);
    const dateRangeType = getDateRangeType(dateRangeOption, selectionType);
    onSelectionChangeHandler &&
      onSelectionChangeHandler(
        newReportDateRange({
          ...dateRangeFilterData,
          DateRange: dateRangeType,
          FromDate: dateRange?.startDate.toDate(),
          NumberOfDays: (dateRange?.endDate.startOf('day').diff(dateRange?.startDate.startOf('day'), 'days') || 0) + 1,
        })
      );
  };

  const onOptionClick = (selectedOption: DateRangeOption) => {
    setSelectedOptions(options => ({ ...options, dateRangeOption: selectedOption }));
    onSelectionChange(selectedOption, dateSelectionType);
  };

  const onSelectionClick = (selectionType: DateSelectionType) => {
    setSelectedOptions(options => ({ ...options, dateSelectionType: selectionType }));
    onSelectionChange(option, selectionType);
  };

  const onCustomDateChange = useCallback(
    (startDate?: moment.Moment, endDate?: moment.Moment) => {
      setStartDate(startDate);
      setEndDate(endDate);
      onSelectionChangeHandler(
        newReportDateRange({
          ...dateRangeFilterData,
          DateRange: DateRangeTypes.Custom,
          FromDate: startDate?.toDate(),
          NumberOfDays: startDate && endDate && endDate.startOf('day').diff(startDate.startOf('day'), 'days') + 1,
        })
      );
    },
    [dateRangeFilterData, onSelectionChangeHandler]
  );

  const getLayer = () => {
    if (showDatePicker) {
      const isValid = endDate?.isSameOrAfter(startDate);
      return (
        <div className="dpc-date-filter-layer__date-picker-holder">
          <div className="dpc-date-filter-layer__date-picker dpc-date-filter-layer__date-picker--start-date">
            <span>From</span>
            <DateTimePicker
              currentDate={startDate}
              isDateTimePicker={false}
              nullable={true}
              onChange={date => onCustomDateChange(date, endDate)}
            />
          </div>
          <div className="dpc-date-filter-layer__date-picker dpc-date-filter-layer__date-picker--end-date">
            <span>To</span>
            <DateTimePicker
              className={classNames({ 'dpc-date-filter-layer__date-picker--invalid': !isValid })}
              currentDate={endDate}
              isDateTimePicker={false}
              nullable={true}
              onChange={date => onCustomDateChange(startDate, date)}
            />
          </div>
          {!isValid && (
            <div className="dpc-date-filter-layer__error-msg">Date cannot take place before "From" date.</div>
          )}
        </div>
      );
    } else if (dateSelectionType) {
      return (
        <CalendarRange
          wrapperClass="dpc-date-filter-layer__calendar"
          headerClass="dpc-date-filter-layer__calendar-header"
          startDate={startDate}
          endDate={endDate}
          selectRange={true}
          onRangeChange={(selectedStartDate, selectedEndDate) => onCustomDateChange(selectedStartDate, selectedEndDate)}
        />
      );
    }
  };

  return (
    <div className="dpc-date-filter-layer__custom">
      <div className="dpc-date-filter-layer__options">
        <button
          className={getButtonClass(DateRangeOption.Previous)}
          onClick={() => onOptionClick(DateRangeOption.Previous)}
        >
          Previous
        </button>
        <button
          className={getButtonClass(DateRangeOption.Current)}
          onClick={() => onOptionClick(DateRangeOption.Current)}
        >
          Current
        </button>
        <button
          className={getButtonClass(DateRangeOption.Future)}
          onClick={() => onOptionClick(DateRangeOption.Future)}
        >
          Future
        </button>
        <button
          className={getButtonClass(DateRangeOption.Custom)}
          onClick={() => onOptionClick(DateRangeOption.Custom)}
        >
          Custom
        </button>
        {!disableRollingRange && (
          <button
            className={getButtonClass(DateRangeOption.Rolling)}
            onClick={() => onOptionClick(DateRangeOption.Rolling)}
          >
            Rolling
          </button>
        )}
      </div>
      {(showDateSelectionType || showDatePicker) && (
        <div className="dpc-date-filter-layer__date-selection">
          {showDateSelectionType && (
            <div className="dpc-date-filter-layer__date-type">
              <button
                className={getDateSelectionTypeClass(DateSelectionType.Week)}
                onClick={() => onSelectionClick(DateSelectionType.Week)}
              >
                Week
              </button>
              <button
                className={getDateSelectionTypeClass(DateSelectionType.Month)}
                onClick={() => onSelectionClick(DateSelectionType.Month)}
              >
                Month
              </button>
              <button
                className={getDateSelectionTypeClass(DateSelectionType.Quarter)}
                onClick={() => onSelectionClick(DateSelectionType.Quarter)}
              >
                Quarter
              </button>
              <button
                className={getDateSelectionTypeClass(DateSelectionType.Year)}
                onClick={() => onSelectionClick(DateSelectionType.Year)}
              >
                Year
              </button>
            </div>
          )}
          {getLayer()}
        </div>
      )}
      {option === DateRangeOption.Rolling && (
        <RollingDateFilter dateRangeFilterData={dateRangeFilterData} onSelectionChange={onSelectionChangeHandler} />
      )}
    </div>
  );
}

const getSelectedOption = (dateRangeType?: DateRangeTypes): SelectedOptions | undefined => {
  if (dateRangeType) {
    switch (dateRangeType) {
      case DateRangeTypes.LastWeek:
        return {
          dateRangeOption: DateRangeOption.Previous,
          dateSelectionType: DateSelectionType.Week,
        };
      case DateRangeTypes.LastMonth:
        return {
          dateRangeOption: DateRangeOption.Previous,
          dateSelectionType: DateSelectionType.Month,
        };
      case DateRangeTypes.LastQuarter:
        return {
          dateRangeOption: DateRangeOption.Previous,
          dateSelectionType: DateSelectionType.Quarter,
        };
      case DateRangeTypes.LastYear:
        return {
          dateRangeOption: DateRangeOption.Previous,
          dateSelectionType: DateSelectionType.Year,
        };
      case DateRangeTypes.ThisWeek:
        return {
          dateRangeOption: DateRangeOption.Current,
          dateSelectionType: DateSelectionType.Week,
        };
      case DateRangeTypes.ThisMonth:
        return {
          dateRangeOption: DateRangeOption.Current,
          dateSelectionType: DateSelectionType.Month,
        };
      case DateRangeTypes.ThisQuarter:
        return {
          dateRangeOption: DateRangeOption.Current,
          dateSelectionType: DateSelectionType.Quarter,
        };
      case DateRangeTypes.ThisYear:
        return {
          dateRangeOption: DateRangeOption.Current,
          dateSelectionType: DateSelectionType.Year,
        };
      case DateRangeTypes.NextWeek:
        return {
          dateRangeOption: DateRangeOption.Future,
          dateSelectionType: DateSelectionType.Week,
        };
      case DateRangeTypes.NextMonth:
        return {
          dateRangeOption: DateRangeOption.Future,
          dateSelectionType: DateSelectionType.Month,
        };
      case DateRangeTypes.NextQuarter:
        return {
          dateRangeOption: DateRangeOption.Future,
          dateSelectionType: DateSelectionType.Quarter,
        };
      case DateRangeTypes.NextYear:
        return {
          dateRangeOption: DateRangeOption.Future,
          dateSelectionType: DateSelectionType.Year,
        };

      case DateRangeTypes.Custom:
        return {
          dateRangeOption: DateRangeOption.Custom,
        };
      case DateRangeTypes.Rolling:
        return {
          dateRangeOption: DateRangeOption.Rolling,
        };
    }
  }
};
